<script lang="ts">
	import ArticleCard from "@components/ArticleCard.svelte";
	import Logo from "@components/Logo.svelte";
	import PostPublishedDate from "@components/PostPublishedDate.svelte";
	import CkEditorContent from "@components/common/CkEditorContent.svelte";
	import ContentBlocks from "@components/common/ContentBlocks.svelte";
	import Heading from "@components/common/Heading.svelte";
	import Media from "@components/common/Media.svelte";
	import StructuredData from "@components/common/StructuredData.svelte";
	import Layout from "@components/layout/Layout.svelte";
	import { getTranslate } from "@utils/getTranslate.js";
	import type { BlogPosting, Organization } from "schema-dts";
	import { thumbUploadedFile } from "@core/utils/thumbUploadedFile.js";
	import { getGlobalSettings } from "../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { idRef, seoFragments, seoId } from "../seo/seoIds.js";
	import translations from "./PostDetail.translations.json" with { type: "json" };
	import type { PostDetailProps } from "./PostDetailProps.js";

	const { post, relatedPosts }: PostDetailProps = $props();
	const { WEB_URL_OR_LOCAL, FILE_BUCKET_URL_MAP } = getGlobalSettings();
	const { tenant, pagePath } = getGlobalTemplateProps();

	const translate = getTranslate(translations);

	const canonicalUrl = $derived(
		tenant.locale in pagePath ? `${WEB_URL_OR_LOCAL}${pagePath[tenant.locale]}` : undefined,
	);

	const imageUrl = $derived(
		post.primaryImage !== undefined ? thumbUploadedFile(1200, 630, post.primaryImage, FILE_BUCKET_URL_MAP) : undefined,
	);

	const organizationRef = $derived(idRef<Organization>(seoId(WEB_URL_OR_LOCAL, seoFragments.organization)));

	const articleSchema = $derived<BlogPosting>({
		"@type": "BlogPosting",
		headline: post.title,
		description: post.perex,
		...(imageUrl !== undefined ? { image: imageUrl } : {}),
		datePublished: post.publishedAt,
		dateModified: post.modifiedAt,
		...(canonicalUrl !== undefined ? { mainEntityOfPage: canonicalUrl } : {}),
		author: organizationRef,
		publisher: organizationRef,
	});
</script>

<StructuredData data={articleSchema} />

<Layout ogImage={post.primaryImage}>
	<div class="container-8 mb-14 md:mb-30 mt-6 md:mt-8">
		<PostPublishedDate {post} />
		<Heading
			level={1}
			text={post.title}
			class={["leading-tighter text-3.5xl font-antipol text-fcrdarkgray my-4", "md:text-4.5xl md:mt-6 md:mb-12"]}
		/>
		<CkEditorContent
			unstyled
			class="text-fcrbrown text-base font-antipol md:text-1.5xl/normal [&_a]:underline"
			html={post.perex}
		/>
	</div>
	{#if post.primaryImage}
		<Media
			alt={post.primaryImage.title ?? ""}
			source={post.primaryImage}
			width={343}
			height={320}
			sourcesets={{
				"<sm": [0, 343],
				">sm": [0, 960],
				">md": [0, 1280],
				">lg": [0, 1600],
				">xl": [0, 1920],
				">2xl": [0, 2400],
				">3xl": [0, 0],
			}}
			wrapperClass="full-width overflow-hidden aspect-[5/3] md:aspect-[21/9]"
			class="object-cover size-full bg-fcrdarkbrown"
		/>
	{:else}
		<div class="full-width aspect-[5/3] md:aspect-[21/9] bg-fcrdarkbrown flex justify-center items-center">
			<Logo class="text-white w-1/3" />
		</div>
	{/if}
	<ContentBlocks class="pt-4 md:pt-30 pb-14 md:pb-40" blocks={post.content} />
	{#if relatedPosts.length > 0}
		<section class="full-width grid-container py-14 md:py-30 bg-fcrbeige">
			<Heading
				level={2}
				class="underline text-fcrdarkgray font-antipol text-3.5xl/normal pb-9 md:pb-18"
				text={translate("moreArticles")}
			/>
			<section class="grid md:grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-14 md:gap-y-18 w-fit mx-auto">
				{#each relatedPosts as post (post.id)}
					<ArticleCard headingLevel={3} {post} huge={false} />
				{/each}
			</section>
		</section>
	{/if}
</Layout>
